import { useState, useEffect, Fragment } from 'react';
import { QUESTIONS } from '../ctfd/quizLogic';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CTFDAdmin() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState('waitlist');
  const [waitlist, setWaitlist] = useState([]);
  const [applications, setApplications] = useState([]);
  const [quizSubs, setQuizSubs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [sortField, setSortField] = useState('submitted_at');
  const [sortDir, setSortDir] = useState('desc');

  const headers = { 'x-admin-password': password };

  const fetchData = async () => {
    const [wRes, aRes, qRes] = await Promise.all([
      fetch(`${API_URL}/api/ctfd/admin/waitlist`, { headers }),
      fetch(`${API_URL}/api/ctfd/admin/applications`, { headers }),
      fetch(`${API_URL}/api/ctfd/admin/quiz`, { headers }),
    ]);
    if (wRes.ok) setWaitlist((await wRes.json()).entries);
    if (aRes.ok) setApplications((await aRes.json()).entries);
    if (qRes.ok) setQuizSubs((await qRes.json()).entries);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    const res = await fetch(`${API_URL}/api/ctfd/admin/waitlist`, { headers: { 'x-admin-password': password } });
    if (res.ok) { setAuthed(true); } else { setAuthError('Incorrect password'); }
  };

  useEffect(() => { if (authed) fetchData(); }, [authed]);

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/ctfd/admin/applications/${id}/status`, {
      method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
  };

  const exportCSV = (type) => {
    window.open(`${API_URL}/api/ctfd/admin/export/${type}?password=${encodeURIComponent(password)}`, '_blank');
  };

  const sorted = (items) => {
    return [...items].sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => (
    <span style={{ opacity: sortField === field ? 1 : 0.3, marginLeft: 4 }}>
      {sortField === field && sortDir === 'asc' ? '\u2191' : '\u2193'}
    </span>
  );

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const statuses = ['New', 'Contacted', 'Call Scheduled', 'Accepted', 'Declined'];
  const statusColors = { New: '#2196F3', Contacted: '#FF9800', 'Call Scheduled': '#9C27B0', Accepted: '#4CAF50', Declined: '#F44336' };

  if (!authed) {
    return (
      <div style={S.page}>
        <div style={S.loginCard}>
          <h2 style={S.loginTitle}>CTFD Admin</h2>
          <form onSubmit={handleLogin}>
            <input type="password" placeholder="Admin password" value={password}
              onChange={e => setPassword(e.target.value)}
              style={S.input} autoFocus data-testid="admin-password" />
            {authError && <p style={S.error}>{authError}</p>}
            <button type="submit" style={S.loginBtn} data-testid="admin-login-btn">Enter</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page} data-testid="admin-dashboard">
      <div style={S.container}>
        <div style={S.header}>
          <h1 style={S.title}>CTFD Submissions</h1>
          <div style={S.tabs}>
            <button onClick={() => setTab('waitlist')} style={tab === 'waitlist' ? S.tabActive : S.tab} data-testid="admin-tab-waitlist">
              Waitlist ({waitlist.length})
            </button>
            <button onClick={() => setTab('applications')} style={tab === 'applications' ? S.tabActive : S.tab} data-testid="admin-tab-applications">
              Applications ({applications.length})
            </button>
            <button onClick={() => setTab('quiz')} style={tab === 'quiz' ? S.tabActive : S.tab} data-testid="admin-tab-quiz">
              Quiz Submissions ({quizSubs.length})
            </button>
          </div>
        </div>

        {tab === 'waitlist' && (
          <div>
            <div style={S.toolbar}>
              <span style={S.count}>{waitlist.length} signups</span>
              <button onClick={() => exportCSV('waitlist')} style={S.exportBtn} data-testid="export-waitlist">Export CSV</button>
            </div>
            <table style={S.table} data-testid="waitlist-table">
              <thead>
                <tr>
                  <th style={S.th} onClick={() => toggleSort('first_name')}>First Name <SortIcon field="first_name" /></th>
                  <th style={S.th} onClick={() => toggleSort('email')}>Email <SortIcon field="email" /></th>
                  <th style={S.th} onClick={() => toggleSort('submitted_at')}>Date Submitted <SortIcon field="submitted_at" /></th>
                </tr>
              </thead>
              <tbody>
                {sorted(waitlist).map(e => (
                  <tr key={e.id} style={S.tr}>
                    <td style={S.td}>{e.first_name}</td>
                    <td style={S.td}>{e.email}</td>
                    <td style={S.td}>{fmtDate(e.submitted_at)}</td>
                  </tr>
                ))}
                {!waitlist.length && <tr><td colSpan={3} style={S.empty}>No waitlist signups yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'applications' && (
          <div>
            <div style={S.toolbar}>
              <span style={S.count}>{applications.length} applications</span>
              <button onClick={() => exportCSV('applications')} style={S.exportBtn} data-testid="export-applications">Export CSV</button>
            </div>
            <table style={S.table} data-testid="applications-table">
              <thead>
                <tr>
                  <th style={S.th} onClick={() => toggleSort('tier')}>Tier <SortIcon field="tier" /></th>
                  <th style={S.th} onClick={() => toggleSort('first_name')}>First Name <SortIcon field="first_name" /></th>
                  <th style={S.th} onClick={() => toggleSort('last_name')}>Last Name <SortIcon field="last_name" /></th>
                  <th style={S.th} onClick={() => toggleSort('email')}>Email <SortIcon field="email" /></th>
                  <th style={S.th} onClick={() => toggleSort('submitted_at')}>Date <SortIcon field="submitted_at" /></th>
                  <th style={S.th} onClick={() => toggleSort('status')}>Status <SortIcon field="status" /></th>
                </tr>
              </thead>
              <tbody>
                {sorted(applications).map(a => (
                  <Fragment key={a.id}>
                    <tr style={{ ...S.tr, cursor: 'pointer', background: expandedId === a.id ? '#F8F7F4' : '' }}
                      onClick={() => setExpandedId(expandedId === a.id ? null : a.id)} data-testid={`app-row-${a.id}`}>
                      <td style={S.td}><span style={{ ...S.tierBadge, background: a.tier.includes('5,000') ? '#1A1A1A' : '#E8590C', color: '#fff' }}>{a.tier.includes('5,000') ? 'IMPL' : 'COHORT'}</span></td>
                      <td style={S.td}>{a.first_name}</td>
                      <td style={S.td}>{a.last_name}</td>
                      <td style={S.td}>{a.email}</td>
                      <td style={S.td}>{fmtDate(a.submitted_at)}</td>
                      <td style={S.td}>
                        <select value={a.status} onChange={e => { e.stopPropagation(); updateStatus(a.id, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          style={{ ...S.statusSelect, color: statusColors[a.status] || '#333' }}
                          data-testid={`status-select-${a.id}`}>
                          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                    {expandedId === a.id && (
                      <tr>
                        <td colSpan={6} style={S.expandedTd}>
                          <div style={S.expandedGrid}>
                            <div style={S.detailItem}><strong>Phone:</strong> {a.phone}</div>
                            {a.website && <div style={S.detailItem}><strong>Website:</strong> {a.website}</div>}
                            {a.social_media && <div style={S.detailItem}><strong>Social:</strong> {a.social_media}</div>}
                            <div style={S.detailQ}><strong>Why do you need to calm the f*ck down?</strong><p>{a.q1_why_calm}</p></div>
                            <div style={S.detailQ}><strong>What will you do with the calm?</strong><p>{a.q2_extra_calm}</p></div>
                            <div style={S.detailQ}><strong>Why now?</strong><p>{a.q3_why_now}</p></div>
                            <div style={S.detailQ}><strong>How will the world benefit?</strong><p>{a.q4_world_benefit}</p></div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!applications.length && <tr><td colSpan={6} style={S.empty}>No applications yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'quiz' && (
          <div>
            <div style={S.toolbar}>
              <span style={S.count}>{quizSubs.length} quiz submissions</span>
              <button onClick={() => exportCSV('quiz')} style={S.exportBtn} data-testid="export-quiz">Export CSV</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table style={S.table} data-testid="quiz-table">
              <thead>
                <tr>
                  <th style={S.th} onClick={() => toggleSort('submitted_at')}>Date <SortIcon field="submitted_at" /></th>
                  <th style={S.th} onClick={() => toggleSort('first_name')}>Name <SortIcon field="first_name" /></th>
                  <th style={S.th} onClick={() => toggleSort('email')}>Email <SortIcon field="email" /></th>
                  <th style={S.th} onClick={() => toggleSort('overall_score')}>Score <SortIcon field="overall_score" /></th>
                  <th style={S.th} onClick={() => toggleSort('archetype')}>Archetype <SortIcon field="archetype" /></th>
                  <th style={S.th} onClick={() => toggleSort('downstream_impact')}>Downstream <SortIcon field="downstream_impact" /></th>
                  <th style={S.th} onClick={() => toggleSort('clicked_apply')}>Applied? <SortIcon field="clicked_apply" /></th>
                </tr>
              </thead>
              <tbody>
                {sorted(quizSubs).map(q => {
                  const di = (q.downstream_impact || '').toLowerCase();
                  const diColor = di === 'high' ? '#D32F2F' : di === 'moderate' ? '#FF9800' : di === 'low' ? '#4CAF50' : '#888';
                  return (
                    <Fragment key={q.id}>
                      <tr style={{ ...S.tr, cursor: 'pointer', background: expandedId === q.id ? '#F8F7F4' : '' }}
                        onClick={() => setExpandedId(expandedId === q.id ? null : q.id)} data-testid={`quiz-row-${q.id}`}>
                        <td style={S.td}>{fmtDate(q.submitted_at)}</td>
                        <td style={S.td}>{[q.first_name, q.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td style={S.td}>{q.email || '—'}</td>
                        <td style={S.td}><strong>{q.overall_score}</strong> <span style={{ color: '#888', fontSize: '0.8rem' }}>· {q.overall_band}</span></td>
                        <td style={S.td}>{q.archetype}</td>
                        <td style={S.td}><span style={{ color: diColor, fontWeight: 600 }}>{q.downstream_impact || '—'}</span></td>
                        <td style={S.td}>{q.clicked_apply ? <span style={{ color: '#4CAF50', fontWeight: 600 }}>Yes</span> : <span style={{ color: '#999' }}>No</span>}</td>
                      </tr>
                      {expandedId === q.id && (
                        <tr>
                          <td colSpan={7} style={S.expandedTd}>
                            <div style={S.expandedGrid}>
                              {q.profile_url && <div style={S.detailItem}><strong>Profile URL:</strong> <a href={q.profile_url.startsWith('http') ? q.profile_url : `https://${q.profile_url}`} target="_blank" rel="noreferrer">{q.profile_url}</a></div>}
                              {q.personal_time_answer && <div style={S.detailItem}><strong>Q16 — where freed-up time would go:</strong> "{q.personal_time_answer}"</div>}
                              <div style={S.detailItem}><strong>Section scores:</strong> Load {q.section_scores?.load} · Recovery Gap {q.section_scores?.recoveryGap} · Fragmentation {q.section_scores?.fragmentation} · Boundary Collapse {q.section_scores?.boundaryCollapse} · Leadership Spillover {q.section_scores?.leadershipSpillover}</div>
                              <div style={S.detailItem}><strong>Recovery Debt:</strong> ~{q.recovery_debt?.debt || 0} hrs/wk &nbsp; · &nbsp; <strong>Attention Leak:</strong> {q.attention_leak?.displayRange || '—'} &nbsp; · &nbsp; <strong>Time Reclaimed:</strong> {q.time_reclaimed?.range || '—'} &nbsp; · &nbsp; <strong>Team Spillover:</strong> ~{q.team_spillover?.teamSpilloverHours || 0} hrs/wk &nbsp; · &nbsp; <strong>Downstream People:</strong> {q.downstream_count || 0}</div>
                              {q.top_drivers?.length > 0 && (
                                <div style={S.detailQ}>
                                  <strong>Top drivers:</strong>
                                  <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{q.top_drivers.map((d, i) => <li key={i} style={{ fontSize: '0.88rem', color: '#555', marginBottom: 4 }}>{d}</li>)}</ul>
                                </div>
                              )}
                              {q.raw_answers?.length > 0 && (
                                <div style={S.detailQ}>
                                  <strong>All answers:</strong>
                                  <div style={{ marginTop: 10 }}>
                                    {q.raw_answers.map((a, i) => (
                                      <div key={i} style={{ padding: '8px 0', borderBottom: i < q.raw_answers.length - 1 ? '1px solid #EEEDE9' : 'none' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#888', fontWeight: 600 }}>Q{i + 1}. {QUESTIONS[i]?.text || ''}</div>
                                        <div style={{ fontSize: '0.95rem', color: '#1A1A1A', marginTop: 3 }}>{a || <em style={{ color: '#bbb' }}>(no answer)</em>}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!quizSubs.length && <tr><td colSpan={7} style={S.empty}>No quiz submissions yet</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#FAFAF8', fontFamily: "'DM Sans', -apple-system, sans-serif", color: '#1A1A1A' },
  container: { maxWidth: 1100, margin: '0 auto', padding: '40px 24px' },
  header: { marginBottom: 32 },
  title: { fontSize: '1.6rem', fontWeight: 700, marginBottom: 16 },
  tabs: { display: 'flex', gap: 0, borderBottom: '1px solid #E0DFDB' },
  tab: { padding: '10px 24px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#888', fontFamily: "'DM Sans', sans-serif" },
  tabActive: { padding: '10px 24px', background: 'none', border: 'none', borderBottom: '2px solid #E8590C', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif" },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 8 },
  count: { fontSize: '0.85rem', color: '#888', fontWeight: 500 },
  exportBtn: { padding: '8px 18px', fontSize: '0.82rem', fontWeight: 600, background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888', borderBottom: '1px solid #E0DFDB', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #F0EFEC' },
  td: { padding: '12px 12px', fontSize: '0.9rem', verticalAlign: 'middle' },
  empty: { padding: 40, textAlign: 'center', color: '#999', fontSize: '0.95rem' },
  tierBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em' },
  statusSelect: { padding: '5px 8px', border: '1px solid #E0DFDB', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, background: '#fff', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  expandedTd: { padding: '0 12px 20px', background: '#F8F7F4' },
  expandedGrid: { padding: '16px 0' },
  detailItem: { fontSize: '0.9rem', marginBottom: 8, color: '#555' },
  detailQ: { marginTop: 16, marginBottom: 0 },
  loginCard: { background: '#fff', padding: '48px 40px', borderRadius: 12, maxWidth: 380, width: '100%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  loginTitle: { fontSize: '1.3rem', fontWeight: 700, marginBottom: 20, textAlign: 'center' },
  input: { width: '100%', padding: '12px 14px', border: '1px solid #E0DFDB', borderRadius: 6, fontSize: '0.95rem', marginBottom: 12, fontFamily: "'DM Sans', sans-serif", background: '#F2F1EE' },
  error: { color: '#D32F2F', fontSize: '0.85rem', marginBottom: 8, textAlign: 'center' },
  loginBtn: { width: '100%', padding: '14px', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
};
